import { guuid, isArray, isBoolean, isFunction, isNeArray, isNeObject, isNeString, isNumber, isObject, isString } from '@aretheyhappy/utils';
import I18N from '../I18N';
import KV from '../KV';
import { BasePermissionConfig } from './IPermissionConfig';
var DASHBOARD_VALIDATION_ERROR;
(function (DASHBOARD_VALIDATION_ERROR) {
    DASHBOARD_VALIDATION_ERROR["NO_ACCESS_DASHBOARD"] = "NO_ACCESS_DASHBOARD";
    DASHBOARD_VALIDATION_ERROR["NO_ACCESS_ROW"] = "NO_ACCESS_ROW";
    DASHBOARD_VALIDATION_ERROR["INVALID_ROW_FOR_DASHBOARD"] = "INVALID_ROW_FOR_DASHBOARD";
    DASHBOARD_VALIDATION_ERROR["NO_ACCESS_WIDGET"] = "NO_ACCESS_WIDGET";
    DASHBOARD_VALIDATION_ERROR["INVALID_WIDGET_FOR_ROW"] = "INVALID_WIDGET_FOR_ROW";
})(DASHBOARD_VALIDATION_ERROR || (DASHBOARD_VALIDATION_ERROR = {}));
/**
 * Dashboard validation result
 */
class DashboardValidationResult {
    constructor(is_valid, reason = undefined, error_code = undefined) {
        this.is_valid = true;
        this.is_valid = is_valid;
        this.reason = reason;
        this.error_code = error_code;
    }
}
/**
 * Check if the sent validation result is valid
 * @param result
 * @returns
 */
const isValidValidationResult = function (result) {
    return isNeObject(result) &&
        isBoolean(result.is_valid) &&
        (isString(result.reason) || result.reason === undefined);
};
/**
 * Helper to hold a list of dashboard validation results
 */
class DashboardValidationResults {
    constructor() {
        this.results = [];
    }
    /**
     * Add a result to the results list
     * @param result
     * @returns
     */
    addResult(result) {
        this.results.push(result);
        return this;
    }
    /**
     * Returns results array
     * @returns
     */
    getResults() {
        return this.results;
    }
    get isValid() {
        for (const result of this.results) {
            if (result.is_valid === false)
                return false;
        }
        return true;
    }
    /**
     * Combines and returns all error messages
     */
    get errorMessages() {
        let messages = '';
        for (const result of this.results) {
            if (isNeString(result.reason)) {
                if (isNeString(messages))
                    messages += '\n';
                messages += result.reason;
            }
        }
        if (isNeString(messages))
            return messages;
        return I18N.go('mead.invalid_configuration');
    }
}
/**
 * Dashboard Definition builder
 *
 * Defines the structure and configuration for a dashboard. Dashboards contain rows,
 * which in turn contain widgets. This builder manages dashboard-level settings including
 * permissions, row types, and default filters.
 */
class DashboardDefinition {
    constructor() {
        // Array of row type keys that are allowed in this dashboard
        this.allowed_row_types = [];
        // Maximum number of rows allowed in this dashboard (0 = unlimited)
        this.max_row_count = 0;
        // Localization key for the dashboard label
        this.label = '';
        // Unique identifier for this dashboard type
        this.type = '';
        // Permission configuration controlling access to this dashboard
        this.permission_config = new BasePermissionConfig();
        // Whether users are allowed to rename rows in this dashboard
        this.can_rename_rows = false;
        // Maximum weight value for dashboard items
        this.max_weight = 100;
        // Default filter settings applied when dashboard loads
        this.default_filters = {};
        // List of validators to use for this dashboard
        this.dashboard_validators = [];
    }
    /**
     * @param {string[]} value - Row type keys to allow
     * @returns {this}
     */
    setAllowedRowTypes(value) { this.allowed_row_types = value; return this; }
    /**
     * @param {string} value - Localization key for the dashboard label
     * @returns {this}
     */
    setLabel(value) { this.label = value; return this; }
    /**
     * @param {string} value - Unique dashboard type identifier
     * @returns {this}
     */
    setType(value) { this.type = value; return this; }
    /**
     * Set the type and key used for loading a component.
     * @deprecated use setType
     * @param value
     * @returns
     */
    setComponentKey(value) { this.type = value; return this; }
    /**
     * @param {IPermissionConfig} value - Permission configuration
     * @returns {this}
     */
    setPermissionConfig(value) { this.permission_config = value; return this; }
    /**
     * @param {number} value - Maximum row count (0 = unlimited)
     * @returns {this}
     */
    setMaxRows(value) { this.max_row_count = value; return this; }
    /** @returns {number} The maximum number of rows allowed */
    getMaxRows() { return this.max_row_count; }
    /** @returns {boolean} True if a row limit is enforced */
    hasRowLimit() { return this.max_row_count > 0; }
    /**
     * @param {boolean} value - Whether to allow row renaming
     * @returns {this} */
    setCanRenameRows(value) { this.can_rename_rows = value; return this; }
    /** @returns {boolean} Whether users can rename rows */
    canRenameRows() { return this.can_rename_rows; }
    /**
     * @param {number} value - Maximum weight
     * @returns {this} */
    setMaxWeight(value) { this.max_weight = value; return this; }
    /** @returns {number} The maximum weight value */
    getMaxWeight() { return this.max_weight; }
    /**
     * @param {Record<string, any>} value - Default filter configuration
     * @returns {this} */
    setDefaultFilters(value) { this.default_filters = value; return this; }
    /** @returns {Record<string, any>} The default filters */
    getDefaultFilters() { return this.default_filters; }
    /**
     * @returns Localized label for the row
     */
    getLabel() {
        return I18N.go(this.label);
    }
    /**
     * Get all row types that the user has permission to access
     * @returns {string[]} Array of accessible row type keys
     */
    getAccessibleRowTypes() {
        const children = [];
        for (const child of this.allowed_row_types) {
            // Look up the row definition and verify user has access
            const row_definition = DashboardService.getRowDefinition(child);
            if (row_definition && row_definition.hasAccess()) {
                children.push(child);
            }
        }
        return children;
    }
    /**
     * Verifies a user has access to this dashboard
    * @returns {boolean} True if the user has access to at least one accessible row type and passes the permission check
     */
    hasAccessToDashboard() {
        return this.getAccessibleRowTypes().length > 0 && this.permission_config.hasAccess();
    }
    /**
     * Set validators for this dashboard definition
     * @param validators
     * @returns
     */
    setDashboardValidators(validators) {
        if (!isArray(validators))
            return this;
        for (const validator of validators) {
            if (!isFunction(validator.isValid)) {
                console.error('validator is missing isValid function', validator);
                return this;
            }
        }
        this.dashboard_validators = validators;
        return this;
    }
    /**
     * Check the validity of this dashboard
     * @param config
     * @param results
     * @returns
     */
    isValid(page, results = new DashboardValidationResults()) {
        // User does not have access to this dashboard type
        if (!this.hasAccessToDashboard()) {
            results.addResult(new DashboardValidationResult(false, undefined, DASHBOARD_VALIDATION_ERROR.NO_ACCESS_DASHBOARD));
            return results;
        }
        // Check all rows
        if (isNeArray(page.rows)) {
            for (const row of page.rows) {
                // Check if this row is valid for this dashboard type.
                if (!this.allowed_row_types.includes(row.type)) {
                    results.addResult(new DashboardValidationResult(false, undefined, DASHBOARD_VALIDATION_ERROR.INVALID_ROW_FOR_DASHBOARD));
                    continue;
                }
                const rowDefinition = DashboardService.getRowDefinition(row.type);
                if (rowDefinition !== undefined) {
                    rowDefinition.isValid(row, results);
                }
                else {
                    console.error(`Invalid row definition "${row.type}" in validation check.`);
                }
            }
        }
        // No internal validators
        if (!isArray(this.dashboard_validators) || this.dashboard_validators.length === 0)
            return results;
        // Check all validators
        for (const validator of this.dashboard_validators) {
            if (!isFunction(validator.isValid))
                continue;
            const result = validator.isValid(page);
            if (isValidValidationResult(result))
                results.addResult(result);
        }
        return results;
    }
}
;
/**
 * Dashboard Row Definition builder
 *
 * Defines the structure and configuration for a row within a dashboard.
 * Rows act as containers for widgets and manage permissions and child widget types.
 */
class DashboardRowDefinition {
    constructor() {
        // Maximum number of child widgets allowed in this row
        this.child_limit = 1;
        // Array of widget type keys that are allowed as children in this row
        this.allowed_child_types = [];
        // Localization key for the row section label that will be used
        // as the default label.
        this.label = '';
        // Unique identifier for this row type
        this.type = '';
        // Permission configuration controlling access to this row
        this.permission_config = new BasePermissionConfig();
        // List of validators to use for this row
        this.row_validators = [];
    }
    /**
     * @param {number} value - Maximum child widget count
     * @returns {this} */
    setChildLimit(value) { this.child_limit = value; return this; }
    /**
     * @param {string[]} value - Widget type keys to allow as children
     * @returns {this} */
    setAllowedChildTypes(value) { this.allowed_child_types = value; return this; }
    /**
     * @param {string} value - Localization key for the row label
     * @returns {this} */
    setLabel(value) { this.label = value; return this; }
    /**
     * @param {string} value - Unique row type identifier
     * @returns {this} */
    setType(value) { this.type = value; return this; }
    /**
     * @param {IPermissionConfig} value - Permission configuration
     * @returns {this} */
    setPermissionConfig(value) { this.permission_config = value; return this; }
    /**
     * @returns Localized label for the row
     */
    getLabel() {
        return I18N.go(this.label);
    }
    /**
     * Get the child limit for this dashboard row type
     * @returns {number} The maximum number of children allowed in this row type
     */
    getLimit() {
        return this.child_limit;
    }
    /**
     * Get all child widget types that the user has permission to access
     * @returns {string[]} Array of accessible child widget type keys
     */
    getAccessibleChildren() {
        const children = [];
        for (const child of this.allowed_child_types) {
            // Look up the widget definition and verify user has access
            const childDefinition = DashboardService.getWidgetDefinition(child);
            if (childDefinition !== undefined && childDefinition.hasAccess()) {
                children.push(child);
            }
        }
        return children;
    }
    /**
     * Check if user has permission to access this row and its children
     * @returns {boolean} True if user has access to at least one child and passes permission check
     */
    hasAccess() {
        return this.getAccessibleChildren().length > 0 && this.permission_config.hasAccess();
    }
    /**
     * Set validators for this row definition
     * @param validators
     * @returns
     */
    setRowValidators(validators) {
        if (!isArray(validators))
            return this;
        for (const validator of validators) {
            if (!isFunction(validator.isValid)) {
                console.error('validator is missing isValid function', validator);
                return this;
            }
        }
        this.row_validators = validators;
        return this;
    }
    /**
     * Check the validity of this row
     * @param config
     * @param results
     * @returns
     */
    isValid(row, results = new DashboardValidationResults()) {
        // User does not have access to this row type
        if (!this.hasAccess()) {
            results.addResult(new DashboardValidationResult(false, undefined, DASHBOARD_VALIDATION_ERROR.NO_ACCESS_ROW));
            return results;
        }
        // Check all row widgets
        if (isNeArray(row.cols)) {
            for (const widget of row.cols) {
                // Check if this row is valid for this dashboard type.
                if (!this.allowed_child_types.includes(widget.widget)) {
                    results.addResult(new DashboardValidationResult(false, undefined, DASHBOARD_VALIDATION_ERROR.INVALID_WIDGET_FOR_ROW));
                    continue;
                }
                const widgetDefinition = DashboardService.getWidgetDefinition(widget.widget);
                if (widgetDefinition !== undefined) {
                    widgetDefinition.isValid(widget, results);
                }
                else {
                    console.error(`Invalid widget definition "${widget.widget}" in validation check.`);
                }
            }
        }
        // No validators
        if (!isNeArray(this.row_validators))
            return results;
        // Check all validators
        for (const validator of this.row_validators) {
            if (!isFunction(validator.isValid))
                continue;
            const result = validator.isValid(row);
            if (isValidValidationResult(result))
                results.addResult(result);
        }
        return results;
    }
}
;
/**
 * Dashboard Widget Definition builder
 *
 * Defines the configuration for a widget that can be added to a dashboard row.
 * Widgets are the lowest-level UI components in the dashboard hierarchy, and this
 * definition controls their configuration appearance, behavior, permissions, and configuration options.
 */
class DashboardWidgetDefinition {
    constructor() {
        // Flexboxgrid CSS classes for widget sizing (e.g., `col-xs-12 col-md-6`)
        this.size_classes = '';
        // Unique identifier for this widget type
        this.type = '';
        // Default properties to apply when creating a new instance of this widget
        this.default_props = {};
        // Localization key for widget title in the add-widget flow
        this.add_widget_title = '';
        // Localization key for widget description in the add-widget flow
        this.add_widget_desc = '';
        // Short display name for the widget type (used in notifications)
        this.basic_type_label = '';
        // DashboardWidgetConfigurationSection array for the widget's settings panel
        this.configuration_widget = [];
        // If this widget has a full screen configuration flow
        this.full_screen_configuration = false;
        // Category grouping for widget organization (e.g., "Charts", "Analytics")
        this.category = '';
        // Weight for this widget type
        this.weight = 0;
        // Array of filter type keys that this widget supports
        this.supported_filters = [];
        // Permission configuration controlling access to this widget
        this.permission_config = new BasePermissionConfig();
        // List of validators to use for this widget
        this.widget_validators = [];
    }
    /**
     * Create a clone of this widget definition
     * @returns {DashboardWidgetDefinition} A new instance with all properties copied
     */
    clone() {
        const widget = new DashboardWidgetDefinition();
        widget.size_classes = this.size_classes;
        widget.type = this.type;
        widget.default_props = Object.assign({}, this.default_props);
        widget.add_widget_title = this.add_widget_title;
        widget.add_widget_desc = this.add_widget_desc;
        widget.basic_type_label = this.basic_type_label;
        if (isArray(this.configuration_widget)) {
            const list = [];
            for (const section of this.configuration_widget) {
                list.push(section.clone());
            }
            widget.configuration_widget = list;
        }
        widget.full_screen_configuration = this.full_screen_configuration;
        widget.category = this.category;
        widget.weight = this.weight;
        widget.supported_filters = [...this.supported_filters];
        widget.permission_config = this.permission_config.clone();
        widget.widget_validators = [...this.widget_validators];
        return widget;
    }
    /**
     * Returns true if there are configuration options for this widget in edit mode
     */
    get hasEditConfiguration() {
        // No configuration widgets defined
        if (!this.hasConfigurationWidgets())
            return false;
        for (const section of this.configuration_widget) {
            if (section.showInEditMode === true)
                return true;
        }
        return false;
    }
    /**
     * Returns true if there are configuration options for this widget in filter mode
     */
    get hasFilterConfiguration() {
        // No configuration widgets defined
        if (!this.hasConfigurationWidgets())
            return false;
        for (const section of this.configuration_widget) {
            if (section.showInFilterMode === true)
                return true;
        }
        return false;
    }
    /**
     * Set size classes from the flexboxgrid library. These control responsive widget sizing
     * and will be applied to the wrapping DashboardItem.vue component.
     * @param {string} value - Flexboxgrid size class string (e.g. `col-xs-12 col-md-6`)
     * @returns {this} The current instance for chaining
     */
    setSizeClasses(value) { this.size_classes = value; return this; }
    /**
     * @param {string} value - Unique widget type identifier
     * @deprecated use setComponentKey
     * @returns {this} */
    setType(value) { this.type = value; return this; }
    /**
     * Set the type and key used for loading a component.
     * @param value
     * @returns
     */
    setComponentKey(value) { this.type = value; return this; }
    /**
     * @param {object} value - Default properties for new widget instances
     * @returns {this} */
    setDefaultProps(value) { this.default_props = value; return this; }
    /**
     * Extend default props with new prop data
     * @param value
     * @returns
     */
    extendDefaultProps(value) {
        if (isObject(this.default_props)) {
            this.default_props = Object.assign(this.default_props, value);
            return this;
        }
        this.default_props = value;
        return this;
    }
    /** @returns {object} A shallow copy of default properties */
    getDefaultProps() { return Object.assign({}, this.default_props); }
    /**
     * @param {string} value - Localization key for widget title
     * @returns {this} */
    setAddWidgetTitle(value) { this.add_widget_title = value; return this; }
    /** @returns {string} The widget add-flow title */
    getAddWidgetTitle() { return this.add_widget_title; }
    /**
     * @param {string} value - Localization key for widget description
     * @returns {this} */
    setAddWidgetDesc(value) { this.add_widget_desc = value; return this; }
    /** @returns {string} The widget add-flow description */
    getAddWidgetDesc() { return this.add_widget_desc; }
    /**
     * The basic type label is used in notifications when adding or updating a widget.
     * @param {string} value - Short display label for the widget type
     * @returns {this} */
    setBasicTypeLabel(value) { this.basic_type_label = value; return this; }
    /** @returns {string} The basic type label */
    getBasicTypeLabel() { return this.basic_type_label; }
    ;
    /**
     * Category grouping for widget organization (e.g., "Charts", "Analytics")
     * When selecting a widget in the add modal, they will be grouped by these types.
     * @param {string} value - Widget category for grouping
     * @returns {this} */
    setCategory(value) { this.category = value; return this; }
    /** @returns {string} The widget category */
    getCategory() { return this.category; }
    ;
    /**
     * @param {number} value - Sort order weight
     * @returns {this} */
    setWeight(value) { this.weight = value; return this; }
    /** @returns {number} The sort weight */
    getWeight() { return this.weight; }
    ;
    /**
     * @param {string[]} value - Supported filter type keys
     * @returns {this} */
    setSupportedFilters(value) { this.supported_filters = value; return this; }
    /** @returns {string[]} The array of supported filter types */
    getSupportedFilters() { return this.supported_filters; }
    /**
     * Set an array of DashboardWidgetConfigurationSection's to show when configuring this widget.
     * @param {DashboardWidgetConfigurationSection[]} value - Configuration array
     * @returns {this}
     */
    setConfigurationList(value) { this.configuration_widget = value; return this; }
    /** @returns {DashboardWidgetConfigurationSection[]} The configuration widgets */
    getConfigurationSections() { return this.configuration_widget; }
    /** @returns {boolean} True if a configuration widget is defined */
    hasConfigurationWidgets() { return this.configuration_widget.length > 0; }
    /**
     * @param {boolean} value - Whether to show configuration in fullscreen
     * @returns {this} */
    setFullscreenInEditMode(value) { this.full_screen_configuration = value; return this; }
    /**
     * If this widget is full screen in edit mode
     * @returns
     */
    isFullScreenInEditMode() { return this.full_screen_configuration === true; }
    /** @returns {string} The size classes for this widget */
    getSizeClasses() { return this.size_classes; }
    /**
     * @param {IPermissionConfig} value - Permission configuration
     * @returns {this} */
    setPermissionConfig(value) { this.permission_config = value; return this; }
    /**
     * Check if user has permission to access this widget
     * @returns {boolean} True if user has access
     */
    hasAccess() {
        return this.permission_config.hasAccess();
    }
    /**
     * Set validators for this widget definition
     * @param validators
     * @returns
     */
    setWidgetValidators(validators) {
        if (!isArray(validators))
            return this;
        for (const validator of validators) {
            if (!isFunction(validator.isValid)) {
                console.error('validator is missing isValid function', validator);
                return this;
            }
        }
        this.widget_validators = validators;
        return this;
    }
    /**
     * Overwrite the configuration for the specified config section or
     * child section that matches the provided component key.
     *
     * Use this if you want to clone a base level definition and
     * update the config for a specific configuration step.
     * @param componentKey - Component key of the section/child to update
     * @param config - New config object to set
     */
    setConfigSectionConfig(componentKey, config) {
        if (!isNeString(componentKey))
            return this;
        if (!isObject(config))
            return this;
        if (!isArray(this.configuration_widget))
            return this;
        // Find the section with the matching component key and overwrite the config
        for (const section of this.configuration_widget) {
            if (section.component_key === componentKey) {
                section.setConfig(config);
                return this;
            }
            else if (isNeArray(section.config_children)) {
                for (const child of section.config_children) {
                    if (child.component_key === componentKey) {
                        child.setConfig(config);
                        return this;
                    }
                }
            }
        }
        return this;
    }
    /**
     * Check the validity of this widget
     * @param config
     * @param results
     * @returns
     */
    isValid(widget, results = new DashboardValidationResults()) {
        // User does not have access to this widget type
        if (!this.hasAccess()) {
            results.addResult(new DashboardValidationResult(false, undefined, DASHBOARD_VALIDATION_ERROR.NO_ACCESS_WIDGET));
            return results;
        }
        // Check all config sections
        if (isNeArray(this.configuration_widget) && isNeObject(widget.options)) {
            this.isConfigValid(widget.options, results);
        }
        // No validators
        if (!isNeArray(this.widget_validators))
            return results;
        // Check all validators
        for (const validator of this.widget_validators) {
            if (!isFunction(validator.isValid))
                continue;
            const result = validator.isValid(widget);
            if (isValidValidationResult(result))
                results.addResult(result);
        }
        return results;
    }
    /**
     * Check the validity of this widget
     * @param config
     * @param results
     * @returns
     */
    isConfigValid(config, results = new DashboardValidationResults()) {
        // Check all config sections
        if (isNeArray(this.configuration_widget) && isNeObject(config)) {
            for (const child of this.configuration_widget) {
                child.isValid(config, results);
            }
        }
        return results;
    }
}
;
/**
 * When configuring a widget on a dashboard. Usually there are multiple different configuration sections
 * on the left hand panel. This is a container for a specific configuration section.
 * The label is what appears on the left, and then when you click it the component
 * is what is rendered to configure the widget.
 */
class DashboardWidgetConfigurationSection {
    constructor() {
        this.section = guuid();
        // Label to display for this section
        this.section_label = '';
        // Component key to load configuration component from
        this.component_key = '';
        // Configation for this section. Will be sent as props to the widget.
        this.config = {};
        // List of validators to use for this configuration section
        this.config_validators = [];
        // If this component should be rendered using the MultiConfigDisplay
        this.is_multi_config = false;
        // If this component should be rendered using the MultiConfigDisplay
        // in its new V2 format that adds titles and subtitles.
        this.is_multi_config_v2 = false;
        // List of configuration children
        this.config_children = [];
        // Should this config section be shown during edit flow
        this.show_while_editing = true;
        // Should this config section be shown during filter flow
        this.show_while_filtering = true;
    }
    clone() {
        const section = new DashboardWidgetConfigurationSection();
        section.section_label = this.section_label;
        section.component_key = this.component_key;
        section.config = Object.assign({}, this.config);
        section.config_validators = this.config_validators;
        section.is_multi_config = this.is_multi_config;
        section.is_multi_config_v2 = this.is_multi_config_v2;
        section.show_while_editing = this.show_while_editing;
        section.show_while_filtering = this.show_while_filtering;
        section.count_function = this.count_function;
        if (isArray(this.config_children)) {
            const list = [];
            for (const child of this.config_children) {
                list.push(child.clone());
            }
            section.config_children = list;
        }
        return section;
    }
    /**
     * Returns true if there are configuration options for this section in edit mode
     */
    get showInEditMode() {
        // Entire section is explitly disabled
        if (this.show_while_editing === false)
            return false;
        if (this.config_children.length === 0)
            return true;
        // If any child is visible in edit mode, then show the section
        for (const child of this.config_children) {
            if (child.showInEditMode === true)
                return true;
        }
        return false;
    }
    /**
     * Returns true if there are configuration options for this section in filter mode
     */
    get showInFilterMode() {
        // Entire section is explitly disabled
        if (this.show_while_filtering === false)
            return false;
        if (this.config_children.length === 0)
            return true;
        // If any child is visible in filter mode, then show the section
        for (const child of this.config_children) {
            if (child.showInFilterMode === true)
                return true;
        }
        return false;
    }
    setLabel(value) { this.section_label = value; return this; }
    setComponentKey(value) { this.component_key = value; return this; }
    /**
     * @deprecated Can be removed. No replacement
     */
    setFilterComponentKey() { return this; }
    setConfig(value) { this.config = value; return this; }
    setConfiguationValidators(validators) {
        if (!isArray(validators))
            return this;
        for (const validator of validators) {
            if (!isFunction(validator.isValid)) {
                console.error('validator is missing isValid function', validator);
                return this;
            }
        }
        this.config_validators = validators;
        return this;
    }
    setIsMultiConfig(value, v2 = false) {
        this.is_multi_config = value;
        this.is_multi_config_v2 = v2;
        return this;
    }
    setConfigChildren(children) {
        if (!isArray(children))
            return this;
        this.config_children = children;
        return this;
    }
    setShowWhileEditing(value) {
        this.show_while_editing = value;
        return this;
    }
    setShowWhileFiltering(value) {
        this.show_while_filtering = value;
        return this;
    }
    /**
     * Set a counting function that when provided a DashboardWidgetConfig
     * will return a count for this section
     * @param configCountFn
     * @returns
     */
    setConfigCountFunction(configCountFn) {
        if (isFunction(configCountFn)) {
            this.count_function = configCountFn;
        }
        return this;
    }
    /**
     * Get this sections configuration count
     * @param config
     * @param editCount - If counts should be for edit mode or filter mode.
     * @returns
     */
    getCount(config = {}, editCount = false) {
        if (editCount === true && !this.showInEditMode)
            return 0;
        if (editCount === false && !this.showInFilterMode)
            return 0;
        let count = 0;
        const section_count = isFunction(this.count_function) ? this.count_function(config) : 0;
        if (isNumber(section_count))
            count += section_count;
        for (const child of this.config_children) {
            count += child.getCount(config, editCount);
        }
        return count;
    }
    /**
     * Check the validity of this configuration
     * @param config
     * @param results
     * @returns
     */
    isValid(config, results = new DashboardValidationResults()) {
        // Check all children
        for (const child of this.config_children)
            child.isValid(config, results);
        // No validators
        if (!isNeArray(this.config_validators))
            return results;
        // Check all validators
        for (const validator of this.config_validators) {
            if (!isFunction(validator.isValid))
                continue;
            const result = validator.isValid(config);
            if (isValidValidationResult(result))
                results.addResult(result);
        }
        return results;
    }
}
;
/**
 * Definition of a child configuration to a section
 */
class DashboardWidgetConfigurationSectionChild {
    constructor() {
        this.uid = guuid();
        // Component key to load configuration component from
        this.component_key = '';
        // Configation for this section. Will be sent as props to the widget.
        this.config = {};
        // List of validators to use for this configuration section
        this.config_validators = [];
        // Should this config section be shown during edit flow
        this.show_while_editing = true;
        // Should this config section be shown during filter flow
        this.show_while_filtering = true;
        // Title for this section child
        this.title = '';
        // Subtitle for this section child
        this.subtitle = '';
    }
    clone() {
        const child = new DashboardWidgetConfigurationSectionChild();
        child.component_key = this.component_key;
        child.config = Object.assign({}, this.config);
        child.config_validators = this.config_validators;
        child.show_while_editing = this.show_while_editing;
        child.show_while_filtering = this.show_while_filtering;
        child.title = this.title;
        child.subtitle = this.subtitle;
        child.count_function = this.count_function;
        return child;
    }
    get showInEditMode() {
        return this.show_while_editing === true;
    }
    get showInFilterMode() {
        return this.show_while_filtering === true;
    }
    setComponentKey(value) { this.component_key = value; return this; }
    /**
     * @deprecated Can be removed. No replacement
     */
    setFilterComponentKey() { return this; }
    setConfig(value) { this.config = value; return this; }
    setConfiguationValidators(validators) {
        if (!isArray(validators))
            return this;
        for (const validator of validators) {
            if (!isFunction(validator.isValid)) {
                console.error('validator is missing isValid function', validator);
                return this;
            }
        }
        this.config_validators = validators;
        return this;
    }
    setShowWhileEditing(value) {
        this.show_while_editing = value;
        return this;
    }
    setShowWhileFiltering(value) {
        this.show_while_filtering = value;
        return this;
    }
    setTitle(value) {
        this.title = value;
        return this;
    }
    setSubtitle(value) {
        this.subtitle = value;
        return this;
    }
    /**
     * Set a counting function that when provided a DashboardWidgetConfig
     * will return a count for this child section
     * @param configCountFn
     * @returns
     */
    setConfigCountFunction(configCountFn) {
        if (isFunction(configCountFn)) {
            this.count_function = configCountFn;
        }
        return this;
    }
    /**
     * Get the configuration count for this section child
     * @param config
     * @param editCount - If counts should be for edit mode or filter mode.
     * @returns
     */
    getCount(config = {}, editCount = false) {
        if (editCount === true && !this.showInEditMode)
            return 0;
        if (editCount === false && !this.showInFilterMode)
            return 0;
        const child_count = isFunction(this.count_function) ? this.count_function(config) : 0;
        if (isNumber(child_count))
            return child_count;
        return 0;
    }
    /**
     * Check the validity of this configuration child
     * @param config
     * @param results
     * @returns
     */
    isValid(config, results = new DashboardValidationResults()) {
        // No validators
        if (!isNeArray(this.config_validators))
            return results;
        // Check all validators
        for (const validator of this.config_validators) {
            if (!isFunction(validator.isValid))
                continue;
            const result = validator.isValid(config);
            if (isValidValidationResult(result))
                results.addResult(result);
        }
        return results;
    }
}
;
/**
 * Handler service that controls data maps for operating a Dashboard in the platform.
 *
 * This is a registry service that manages three levels of dashboard components:
 * - Dashboards (top level, contains rows)
 * - Rows (middle level, contains widgets)
 * - Widgets (lowest level, individual UI components)
 *
 * The service must be populated with definitions before a dashboard can load correctly.
 * Use the static methods to register and retrieve dashboard, row, and widget definitions.
 */
class DashboardService {
    static clearAll() {
        this.DASHBOARD_MAP.clear();
        this.ROW_MAP.clear();
        this.WIDGET_MAP.clear();
    }
    /**
     * Register one or more dashboard system definitions.
     *
     * Supports a single definition instance or an array of mixed definition types.
     *
     * @param definitions - A single definition or an array of definitions to register.
     * @returns {void}
     */
    static addDefinition(definitions) {
        // Array of mixed types, import all of them
        if (Array.isArray(definitions)) {
            for (const definition of definitions) {
                this.addDefinition(definition);
            }
            return;
        }
        const type = definitions.type;
        if (!isNeString(type)) {
            console.error(`DashboardService.addDefinition: definition.type must be a non empty string. Provided value was: ${type}`);
            return;
        }
        // Dashboard Definition
        if (definitions instanceof DashboardDefinition) {
            this.DASHBOARD_MAP.set(type, definitions);
        }
        // Row Definition
        else if (definitions instanceof DashboardRowDefinition) {
            this.ROW_MAP.set(type, definitions);
        }
        // Widget Definition
        else if (definitions instanceof DashboardWidgetDefinition) {
            this.WIDGET_MAP.set(type, definitions);
        }
        else {
            const providedType = definitions?.constructor?.name ?? typeof definitions;
            console.error(`DashboardService.addDefinition: unsupported definition type. Provided value was: ${providedType}`);
        }
    }
    /**
     * Set a dashboard definition on the service
     * @param type - Unique string key identifying the dashboard
     * @param definition - The `DashboardDefinition` instance to register
     * @deprecated - Use addDefinition
     */
    static addDashboardDefinition(type, definition) {
        if (!isNeString(type)) {
            console.error(`DashboardService.addDashboardDefinition: type must be a non empty string. Provided value was: ${type}`);
        }
        this.DASHBOARD_MAP.set(type, definition);
    }
    /**
     * Fetch a dashboard by type
     * @param type - The dashboard type key to look up
     * @returns {DashboardDefinition | undefined} The matching definition, or `undefined` if not registered
     */
    static getDashboardDefinition(type) {
        if (!isNeString(type)) {
            console.error(`DashboardService.getDashboardDefinition: type must be a non empty string. Provided value was: ${type}`);
        }
        // Return the dashboard if it exists in the map
        if (this.DASHBOARD_MAP.has(type))
            return this.DASHBOARD_MAP.get(type);
        console.error(`Dashboard ${type} not in map`);
        return;
    }
    /**
     * Add a new row definition to the map
     * @param type - Unique string key identifying the row type
     * @param definition - The `DashboardRowDefinition` instance to register
     * @deprecated - Use addDefinition
     */
    static addRowDefinition(type, definition) {
        if (!isNeString(type)) {
            console.error(`DashboardService.addRowDefinition: type must be a non empty string. Provided value was: ${type}`);
        }
        this.ROW_MAP.set(type, definition);
    }
    /**
     * Fetch a row type
    * @param type - The row type key to look up
    * @returns {DashboardRowDefinition | undefined} The matching definition, or `undefined` if not registered
     */
    static getRowDefinition(type) {
        if (!isNeString(type)) {
            console.error(`DashboardService.getRowDefinition: type must be a non empty string. Provided value was: ${type}`);
        }
        // Return the row definition if it exists in the map
        if (this.ROW_MAP.has(type))
            return this.ROW_MAP.get(type);
        console.error(`Dashboard row ${type} not in map`);
        return;
    }
    /**
     * Add a new widget definition to the map
     * @param type - Unique string key identifying the widget type
     * @param definition - The `DashboardWidgetDefinition` instance to register
     * @deprecated - Use addDefinition
     */
    static addWidgetDefinition(type, definition) {
        if (!isNeString(type)) {
            console.error(`DashboardService.addWidgetDefinition: type must be a non empty string. Provided value was: ${type}`);
        }
        this.WIDGET_MAP.set(type, definition);
    }
    /**
     * Check if a widget definition is in the map
     * @param type
     * @returns
     */
    static hasWidgetDefinition(type) {
        if (!isNeString(type)) {
            console.error(`DashboardService.hasWidgetDefinition: type must be a non empty string. Provided value was: ${type}`);
        }
        return this.WIDGET_MAP.has(type);
    }
    /**
     * Fetch a widget type
     * @param type - The widget type key to look up
     * @returns {DashboardWidgetDefinition | undefined} The matching definition, or `undefined` if not registered
     */
    static getWidgetDefinition(type) {
        if (!isNeString(type)) {
            console.error(`DashboardService.getWidgetDefinition: type must be a non empty string. Provided value was: ${type}`);
        }
        // Return the widget definition if it exists in the map
        if (this.WIDGET_MAP.has(type))
            return this.WIDGET_MAP.get(type);
        console.error(`Dashboard widget ${type} not in map`);
        return;
    }
    /**
     * Register a new dashboard widget component for use on dashboards.
     * This registers the actual Vue component that will be rendered inside a dashboard item.
     * Components are stored in the KV (key-value) store and should be registered early
     * during application initialization.
     * @param {string} key - Unique key for the widget component
     * @param {any} component - Vue component to register
     */
    static registerDashboardWidgetComponent(key, component) {
        if (!isNeString(key)) {
            console.error(`DashboardService.registerDashboardWidgetComponent: type must be a non empty string. Provided value was: ${key}`);
        }
        // Only register if not already registered to prevent overwrites
        if (!KV.has(key))
            KV.set(key, component);
    }
    /**
     * Check if a component is registered with the sent key
     * @param {string} key - Unique key for the widget component
     * @returns {boolean}
     */
    static hasDashboardWidgetComponent(key) {
        // Check if widget component is registered
        return KV.has(key);
    }
    /**
     * Fetch a dashboard widget by key
     * @param {string} key - Unique key for the widget component
     * @returns {any} The registered Vue component, or `undefined` if the key has not been registered
     */
    static getDashboardWidgetComponent(key) {
        // Check if widget component is registered, log error if not found
        if (!KV.has(key)) {
            console.error(`Unregistered widget requested with id ${key}`);
            return;
        }
        return KV.get(key);
    }
}
// Registry map for all dashboard definitions, keyed by dashboard type
DashboardService.DASHBOARD_MAP = new Map();
// Registry map for all row definitions, keyed by row type
DashboardService.ROW_MAP = new Map();
// Registry map for all widget definitions, keyed by widget type
DashboardService.WIDGET_MAP = new Map();
/**
 * Generic dashboard helper utilities.
 *
 * These helpers derive metadata from an instantiated dashboard layout rather than
 * from a static dashboard definition map.
 */
class DashboardHelper {
    /**
     * Calculate the unique set of filters supported by widgets present in a dashboard layout.
     *
     * The method inspects each row/panel, reads all widget types, then resolves each widget
     * definition from `DashboardService.getWidgetDefinition` and accumulates supported filter keys.
     * Unregistered widget types are ignored.
     *
     * @param {DataStudioRow[]} dashboardRows - Dashboard rows containing widget instances.
     * @returns {string[]} A de-duplicated list of supported filter keys in first-seen order.
     */
    static calculateFiltersBasedOnDashboard(dashboardRows) {
        const widgetTypes = [];
        for (const panel of dashboardRows) {
            for (const widget of panel.cols) {
                widgetTypes.push(widget.widget);
            }
        }
        const supported_filters = [];
        for (const widget of widgetTypes) {
            const widgetDefinition = DashboardService.getWidgetDefinition(widget);
            if (widgetDefinition === undefined)
                continue;
            const filters = widgetDefinition.getSupportedFilters();
            for (const filter of filters) {
                if (!supported_filters.includes(filter))
                    supported_filters.push(filter);
            }
        }
        return supported_filters;
    }
}
export { DASHBOARD_VALIDATION_ERROR, DashboardService, DashboardDefinition, DashboardRowDefinition, DashboardWidgetDefinition, DashboardWidgetConfigurationSection, DashboardWidgetConfigurationSectionChild, DashboardHelper, DashboardValidationResult, DashboardValidationResults, };
export default DashboardService;
//# sourceMappingURL=DashboardService.js.map